/**
 * The UPB section of Trade economics — sits BELOW the Current economics and
 * Recompute inputs cards.
 *
 *   ┌ Current UPB · Purchase basis ─┐ ┌ stat grid (2 × 3) ──────────┐
 *   └───────────────────────────────┘ └─────────────────────────────┘
 *   ┌ UPB over time ───────────────────────────────────────────────┐
 *
 * The lead card and the chart are the SAME components the Summary tab renders
 * (shared/upb), so the two tabs can't disagree. Every tile here reads a field
 * on LoanAcquisitionBaseModel; none of them repeats a figure already in the
 * page's KPI strip (funding wire, purchase price, accrued interest, strip).
 */
import PropTypes from "prop-types";
import Box from "@mui/material/Box";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { TAB_TOKENS as T } from "../../shared/tabLayout/tabTokens";
import UpbLeadCard from "../../shared/upb/UpbLeadCard";
import UpbOverTimeCard from "../../shared/upb/UpbOverTimeCard";
import {
  money,
  monthsBetween,
  num,
  shortDate,
  toDay,
  upbFacts,
} from "../../shared/upb/upbModel";
import { formatPercent } from "../../../../utils/numbers/formatPercent";

const StatTile = ({ label, value, sub }) => (
  <Flx column sx={{ p: "16px 18px", gap: "4px", minWidth: 0 }}>
    <Txt sx={{ fontSize: 12.5, color: T.color.ink3 }}>{label}</Txt>
    <Txt
      sx={{
        fontSize: 19,
        fontWeight: 650,
        color: value == null ? T.color.muted : T.color.ink,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {value ?? "—"}
    </Txt>
    {sub && <Txt sx={{ fontSize: 12, color: T.color.muted }}>{sub}</Txt>}
  </Flx>
);

StatTile.propTypes = {
  label: PropTypes.node,
  value: PropTypes.node,
  sub: PropTypes.node,
};

const StatGrid = ({ loan }) => {
  const f = upbFacts(loan);
  const remaining =
    f.maturity && f.cutOff
      ? Math.max(0, monthsBetween(f.cutOff, f.maturity))
      : null;
  const nextDue = toDay(loan?.nextPaymentDueDate || loan?.nextPaymentDate);
  const holdbackLeft = num(loan?.purchasedAsset?.undisbursedHoldback);
  const reserve = num(loan?.purchasedAsset?.currentInterestReserve);

  const tiles = [
    {
      label: "Note rate",
      value: f.rate == null ? null : formatPercent(f.rate),
      sub: f.amortizationType,
    },
    {
      label: "Maturity",
      value: shortDate(f.maturity),
      sub: remaining == null ? null : `${remaining} months after cut-off`,
    },
    {
      label: "Next payment due",
      value: shortDate(nextDue),
      sub: null,
    },
    {
      label: "Paid to",
      value: shortDate(f.paidTo),
      sub:
        f.accruedDays == null
          ? null
          : `${f.accruedDays} days of interest accrued to cut-off`,
    },
    {
      label: "Total loan amount",
      value: money(f.fullyDrawn),
      sub:
        holdbackLeft == null
          ? "The balance if fully drawn"
          : `${money(holdbackLeft)} holdback left to draw`,
    },
    {
      label: "Interest reserve",
      value: money(reserve),
      sub: reserve == null ? null : "Balance at settlement",
    },
  ];

  return (
    <Box
      sx={{
        background: T.color.surface,
        border: `1px solid ${T.color.border}`,
        borderRadius: T.radius.card,
        boxShadow: T.shadow.card,
        overflow: "hidden",
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        alignContent: "stretch",
        "& > *": {
          borderBottom: `1px solid ${T.color.border}`,
          "&:nth-of-type(odd)": { borderRight: `1px solid ${T.color.border}` },
          "&:nth-last-of-type(-n+2)": { borderBottom: "none" },
        },
        "@media (max-width: 480px)": {
          gridTemplateColumns: "1fr",
          "& > *": {
            borderRight: "none !important",
            borderBottom: `1px solid ${T.color.border}`,
          },
        },
      }}
    >
      {tiles.map((t) => (
        <StatTile key={t.label} {...t} />
      ))}
    </Box>
  );
};

StatGrid.propTypes = { loan: PropTypes.object };

const UpbOverview = ({ loan }) => (
  <Flx column sx={{ gap: "24px" }}>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1.15fr 1fr",
        gap: "24px",
        alignItems: "start",
        "@media (max-width: 960px)": { gridTemplateColumns: "1fr" },
      }}
    >
      <UpbLeadCard
        loan={loan}
        sx={{ borderRadius: T.radius.card, boxShadow: T.shadow.card }}
      />
      <StatGrid loan={loan} />
    </Box>
    <UpbOverTimeCard loan={loan} />
  </Flx>
);

UpbOverview.propTypes = { loan: PropTypes.object };

export default UpbOverview;
